import { useQuery } from "@apollo/client/react";
import { Badge, Variant as BadgeVariant } from "@leafygreen-ui/badge";
import { Button, Size as ButtonSize } from "@leafygreen-ui/button";
import { InfoSprinkle } from "@leafygreen-ui/info-sprinkle";
import { Skeleton, Size as SkeletonSize } from "@leafygreen-ui/skeleton-loader";
import { BaseFontSize } from "@leafygreen-ui/tokens";
import { Link } from "react-router-dom";
import { TaskStatus } from "@evg-ui/lib/types/task";
import { getTaskRoute } from "constants/routes";
import {
  StepbackTasksQuery,
  StepbackTasksQueryVariables,
} from "gql/generated/types";
import { STEPBACK_TASKS } from "gql/queries";
import styles from "./index.module.css";

interface StepbackStatusProps {
  finished: boolean;
  isLoading: boolean;
}

const StepbackStatus: React.FC<StepbackStatusProps> = ({
  finished,
  isLoading,
}) => {
  if (isLoading) {
    return <Skeleton size={SkeletonSize.Small} />;
  }
  if (!finished) {
    return <Badge variant={BadgeVariant.LightGray}>In progress</Badge>;
  }
  return <Badge variant={BadgeVariant.Green}>Complete</Badge>;
};

interface StepbackProps {
  execution: number;
  isPopup?: boolean;
  status: string;
  taskId: string;
}

export const Stepback: React.FC<StepbackProps> = ({
  execution,
  isPopup = false,
  status,
  taskId,
}) => {
  const { data, loading } = useQuery<
    StepbackTasksQuery,
    StepbackTasksQueryVariables
  >(STEPBACK_TASKS, {
    variables: {
      taskId,
      execution,
      isPassing: status === TaskStatus.Succeeded,
    },
  });

  const breakingTask = data?.task?.prevTaskPassing?.nextTaskFailing;
  const currentTaskIsBreaking = breakingTask?.id === taskId;

  // Stepback is finished if there is a breaking task.
  const finished = !!breakingTask;

  return (
    <div className={styles.stepbackLabel}>
      <b className={styles.boldLabel}>Stepback: </b>
      <StepbackStatus finished={finished} isLoading={loading} />
      {!isPopup && (
        <InfoSprinkle baseFontSize={BaseFontSize.Body1}>
          When Stepback is completed you can access the breaking commit via the
          Stepback dropdown.
        </InfoSprinkle>
      )}
      {isPopup &&
        (currentTaskIsBreaking ? (
          <em>Current task is breaking</em>
        ) : (
          <Button
            as={Link}
            className={styles.breakingTaskButton}
            data-testid="breaking-task-button"
            disabled={
              loading || !finished || !breakingTask || currentTaskIsBreaking
            }
            size={ButtonSize.Small}
            to={
              breakingTask
                ? getTaskRoute(breakingTask.id, {
                    execution: breakingTask.execution,
                  })
                : ""
            }
          >
            Go to breaking task
          </Button>
        ))}
    </div>
  );
};
